import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

// Configuracoes de IA (provedor por usuario + chave do Gemini) ficam no v1 (Express), /api/ai/*.
const V1_BASE = '/api'
function v1Headers(): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  const token = localStorage.getItem('auth_token')
  if (token) headers['Authorization'] = `Bearer ${token}`
  return headers
}

async function v1Request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${V1_BASE}${path}`, { ...init, headers: v1Headers() })
  const body = await res.json().catch(() => ({}))
  if (!res.ok) throw new Error((body as { error?: string }).error || `HTTP ${res.status}`)
  return body as T
}

export type AiProvider = 'chatgpt' | 'gemini'

export interface AiSettings {
  provider: AiProvider
  chatgpt: { connected: boolean }
  gemini: {
    configured: boolean
    model: string
    keyLast4: string | null
    connectedAt: string | null
  }
}

export interface GeminiModel {
  name: string
  displayName: string
}

export const AI_SETTINGS_KEY = ['ai', 'settings'] as const

export function useAiSettings() {
  return useQuery<AiSettings>({
    queryKey: AI_SETTINGS_KEY,
    queryFn: () => v1Request('/ai/settings'),
    staleTime: 30 * 1000,
  })
}

function useInvalidateAi() {
  const qc = useQueryClient()
  return () => {
    qc.invalidateQueries({ queryKey: AI_SETTINGS_KEY })
    // A varinha de IA nas telas le este status; ele muda junto com o provedor
    qc.invalidateQueries({ queryKey: ['openai', 'status'] })
  }
}

export function useSetAiProvider() {
  const invalidate = useInvalidateAi()
  return useMutation<AiSettings, Error, AiProvider>({
    mutationFn: (provider) => v1Request('/ai/provider', { method: 'POST', body: JSON.stringify({ provider }) }),
    onSuccess: invalidate,
  })
}

// Salva a chave (e/ou o modelo). O backend testa a chave antes de gravar.
export function useSaveGeminiKey() {
  const invalidate = useInvalidateAi()
  return useMutation<AiSettings, Error, { key?: string; model?: string }>({
    mutationFn: (params) => v1Request('/ai/gemini/key', { method: 'POST', body: JSON.stringify(params) }),
    onSuccess: invalidate,
  })
}

export function useRemoveGeminiKey() {
  const invalidate = useInvalidateAi()
  return useMutation<AiSettings, Error, void>({
    mutationFn: () => v1Request('/ai/gemini/key', { method: 'DELETE' }),
    onSuccess: invalidate,
  })
}

// Lista de modelos que a chave enxerga (a do formulario, ou a salva quando key vazia)
export function useGeminiModels() {
  return useMutation<{ models: GeminiModel[] }, Error, { key?: string }>({
    mutationFn: (params) => v1Request('/ai/gemini/models', { method: 'POST', body: JSON.stringify(params) }),
  })
}
